(() => {
  'use strict';
  if (window.HBEmptyCollections) { window.HBEmptyCollections.refresh(); return; }
  const config = document.querySelector('script[data-hb-empty-collections]');
  const serverCountsAt = Date.now();
  const shopOrigins = new Set([location.origin]);
  for (const address of [config?.dataset.shopUrl, config?.dataset.shopDomain]) {
    if (address) { try { shopOrigins.add(new URL(address.includes('://') ? address : 'https://' + address).origin); } catch (_) {} }
  }
  const menuSelector = '.hbg-menu,.hbg-drawer-nav,.hbg-mobile-universes,header nav,footer nav,.menu-drawer__navigation';
  const roots = new Map();
  const counts = new Map();
  const bindings = new Map();
  const controllers = new Map();
  const hiddenState = new WeakMap();
  const sliderUpdates = new WeakSet();
  const pending = [];
  let active = 0;
  let scheduled = false;
  let generation = 0;

  function collectionURL(raw) {
    if (!raw) return null;
    try {
      const original = new URL(raw, location.href);
      if (!shopOrigins.has(original.origin)) return null;
      const match = original.pathname.match(/^(.*?)\/collections\/([^/]+)(?:\/([^/]+))?\/?$/);
      if (!match || match[3] === 'products' || ['types','vendors'].includes(match[2])) return null;
      const handle = decodeURIComponent(match[2]);
      const localeRoot = window.Shopify?.routes?.root || config?.dataset.rootUrl || '/';
      const prefix = match[1] ? match[1] + '/' : localeRoot;
      const url = new URL(prefix.replace(/\/?$/, '/') + 'collections/' + encodeURIComponent(handle), location.origin);
      url.searchParams.set('section_id', 'hb-collection-visibility');
      const here = new URL(location.href);
      const theme = window.Shopify?.theme;
      const preview = here.searchParams.get('preview_theme_id') ||
        (theme?.id && theme.role && theme.role !== 'main' ? String(theme.id) : '');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      for (const key of ['country','currency','locale']) {
        const value = here.searchParams.get(key);
        if (value) url.searchParams.set(key, value);
      }
      return {url, handle, key:url.href};
    } catch (_) { return null; }
  }

  function pump() {
    while (active < 2 && pending.length) {
      const job = pending.shift();
      active++;
      job().finally(() => { active--; pump(); });
    }
  }

  function countFor(info) {
    if (!counts.has(info.key)) {
      const promise = new Promise(resolve => {
        pending.push(async () => {
          let count = null;
          for (let attempt = 0; attempt < 2; attempt++) {
            const request = new AbortController();
            // Une prévisualisation Shopify peut dépasser 7 secondes.
            const timer = setTimeout(() => request.abort(), 20000);
            let retry = false;
            let delay = 1200;
            try {
              const response = await fetch(info.url.href, {credentials:'same-origin',cache:'no-store',signal:request.signal});
              if (response.ok) {
                const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
                const marker = doc.querySelector('[data-hb-collection-visibility]');
                const value = marker?.getAttribute('data-count');
                if (marker?.getAttribute('data-handle') === info.handle && /^\d+$/.test(value || '')) {
                  count = Number(value);
                }
              } else {
                retry = response.status === 408 || response.status === 429 || response.status >= 500;
                const after = response.headers.get('Retry-After');
                if (after && /^\d+$/.test(after)) {
                  delay = Math.min(10000, Math.max(delay, Number(after) * 1000));
                }
              }
            } catch (_) {
              retry = true;
            } finally {
              clearTimeout(timer);
            }
            // Seul un zéro confirmé autorise le masquage. Une 404 ou une réponse
            // invalide restent indéterminées et ne provoquent pas de répétitions.
            if (count !== null || !retry || attempt === 1) break;
            await new Promise(done => setTimeout(done, delay));
          }
          resolve(count);
        });
        pump();
      });
      counts.set(info.key, promise);
    }
    return counts.get(info.key);
  }

  function hide(node, empty) {
    if (!node) return;
    let changed = false;
    if (empty && !hiddenState.has(node)) {
      hiddenState.set(node, {hidden:node.hidden,display:node.style.getPropertyValue('display'),priority:node.style.getPropertyPriority('display')});
      node.hidden = true;
      node.dataset.hbEmptyHidden = '';
      node.style.setProperty('display', 'none', 'important');
      node.querySelectorAll('details[open]').forEach(details=>{details.open=false;});
      changed = true;
    } else if (!empty && hiddenState.has(node)) {
      const old = hiddenState.get(node);
      node.hidden = old.hidden;
      if (old.display) node.style.setProperty('display',old.display,old.priority);
      else node.style.removeProperty('display');
      delete node.dataset.hbEmptyHidden;
      hiddenState.delete(node);
      changed = true;
    }
    const slider = changed && node.closest('slider-component');
    if (slider && typeof slider.resetPages === 'function' && !sliderUpdates.has(slider)) {
      sliderUpdates.add(slider);
      requestAnimationFrame(() => {
        sliderUpdates.delete(slider);
        if (!slider.isConnected) return;
        slider.resetPages();
        if (slider.sliderItemsToShow?.length < 2) {
          slider.totalPages = slider.sliderItemsToShow.length;
          if (slider.pageTotalElement) slider.pageTotalElement.textContent = String(slider.totalPages);
          if (slider.currentPageElement) slider.currentPageElement.textContent = String(slider.totalPages);
          if (slider.prevButton) slider.prevButton.disabled = true;
          if (slider.nextButton) slider.nextButton.disabled = true;
        }
      });
    }
  }

  function bind(node, raw, apply) {
    const info = collectionURL(raw);
    const old = bindings.get(node);
    if (!info) {
      if (old) { bindings.delete(node); old.apply(false); }
      return;
    }
    if (old?.key === info.key && old.generation === generation) return;
    const binding = {key:info.key,generation,apply,empty:false};
    bindings.set(node,binding);
    countFor(info).then(count => {
      if (!node.isConnected || bindings.get(node) !== binding) return;
      binding.empty = count === 0;
      apply(binding.empty);
      schedule();
    });
  }

  function syncController(controller, state) {
    if (!controller.isConnected) return;
    const current = controller.tabs.find(tab => tab.getAttribute('aria-selected') === 'true');
    const focus = controller.getRootNode().activeElement;
    const visible = state.pairs.filter(pair => !pair.empty);
    for (const pair of state.pairs) {
      hide(pair.tab, pair.empty);
      hide(pair.panel, pair.empty);
      if (pair.empty) {
        pair.tab.setAttribute('aria-selected','false');
        pair.tab.tabIndex = -1;
        pair.panel.request?.abort();
        pair.panel.loading = false;
      }
    }
    controller.tabs = visible.map(pair => pair.tab);
    controller.panels = visible.map(pair => pair.panel);
    if (!state.message && state.pairs.length) {
      state.message = document.createElement('p');
      state.message.textContent = 'Aucune collection disponible pour le moment.';
      state.message.dataset.hbEmptyMessage = '';
      state.message.setAttribute('role','status');
      state.message.hidden = true;
      state.pairs[0].panel.parentElement.append(state.message);
    }
    if (state.message) state.message.hidden = visible.length > 0;
    if (visible.length) {
      const index = Math.max(0, controller.tabs.indexOf(current));
      // Garder le choix et les filtres en cours. Réactiver uniquement si nécessaire.
      if (!current || !controller.tabs.includes(current)) controller.activate(index);
      else visible.forEach((pair,i) => { pair.panel.hidden = i !== index;pair.tab.tabIndex = i === index ? 0 : -1; });
      if (state.pairs.some(pair => pair.empty && (pair.tab === focus || pair.panel.contains(focus)))) {
        controller.tabs[index].focus({preventScroll:true});
      }
    }
  }

  function scanController(controller) {
    if (!controller.ready || !Array.isArray(controller.tabs) || !Array.isArray(controller.panels) ||
        typeof controller.activate !== 'function') return;
    let state = controllers.get(controller);
    if (!state) {
      state = {pairs:controller.tabs.map((tab,index) => ({tab,panel:controller.panels[index],empty:false})).filter(pair=>pair.panel)};
      controllers.set(controller,state);
    }
    for (const pair of state.pairs) {
      // Une catégorie qui ouvre une PAGE n'est pas une collection vide.
      const raw = pair.panel.dataset.destination === 'page' ? '' : pair.panel.dataset.collectionUrl;
      bind(pair.tab,raw,empty => { if (pair.empty !== empty) { pair.empty=empty;syncController(controller,state); } });
    }
  }

  function cardFor(link) {
    const wrapper = link.closest('.collection-list__item,.hb-collection-card');
    if (wrapper) return wrapper;
    let candidate = link;
    for (let n=0; candidate && n<4; n++,candidate=candidate.parentElement) {
      if ([...candidate.classList].some(c=>/^hb[a-z0-9]*-(category|card)$/.test(c))) return candidate;
    }
    return null;
  }

  function scanLinks(root) {
    root.querySelectorAll('a[href*="/collections/"]').forEach(link => {
      const menu = link.closest(menuSelector);
      if (menu) {
        bind(link,link.getAttribute('href'),empty => {
          if (empty) link.dataset.hbEmptyLink = '';
          else delete link.dataset.hbEmptyLink;
          // Les groupes avec des enfants non vides restent des points de navigation.
          if (link.closest('li')?.querySelector('ul')) hide(link,empty);
        });
      } else {
        const card = cardFor(link);
        if (card) bind(link,link.getAttribute('href'),empty=>hide(card,empty));
      }
    });
    root.querySelectorAll(menuSelector).forEach(menu => {
      [...menu.querySelectorAll('li')].reverse().forEach(item => {
        const lists = [...item.querySelectorAll('ul')].filter(list => list.closest('li') === item);
        const children = lists.flatMap(list => [...list.children].filter(child=>child.tagName==='LI'));
        const own = [...item.querySelectorAll('a[href]')].filter(link => link.closest('li') === item && !link.closest('.hbg-mega-footer'));
        const ownIsEmpty = own.length > 0 && own.every(link=>link.hasAttribute('data-hb-empty-link'));
        const hasChildren = children.length > 0;
        const childrenAreEmpty = hasChildren && children.every(child=>child.hasAttribute('data-hb-empty-hidden'));
        const hasRealOwnLink = own.some(link => {
          const href = link.getAttribute('href');
          return href && href !== '#' && !link.hasAttribute('data-hb-empty-link');
        });
        hide(item, hasChildren ? childrenAreEmpty && !hasRealOwnLink : ownIsEmpty);
      });
    });
  }

  function scan(root) {
    // Fresh Liquid counts are authoritative. On an old back/forward document,
    // refresh normally instead of hiding based on stale server markup.
    if (Date.now() - serverCountsAt < 60000) {
      root.querySelectorAll('[data-hb-collection-count][data-hb-collection-handle]').forEach(node => {
        const info = collectionURL(node.dataset.collectionUrl || node.getAttribute('href'));
        const value = node.dataset.hbCollectionCount;
        if (info && info.handle === node.dataset.hbCollectionHandle && /^\d+$/.test(value)) {
          counts.set(info.key, Promise.resolve(Number(value)));
        }
      });
    }
    const owners = new Set();
    root.querySelectorAll('[data-collection-url]').forEach(panel => {
      let node = panel;
      while (node) {
        if (node.localName?.startsWith('hb-') && Array.isArray(node.tabs)) { owners.add(node);break; }
        node = node.parentElement;
      }
    });
    owners.forEach(scanController);
    scanLinks(root);
    root.querySelectorAll('.hbui-inline-page,[data-hb-inline-page]').forEach(host => {
      if (host.shadowRoot) register(host.shadowRoot);
    });
  }

  function register(root) {
    if (!roots.has(root)) {
      const observer = new MutationObserver(records => {
        // Text counters and stock/status messages do not change navigation.
        if (records.some(record => record.type === 'attributes' ||
          [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1))) schedule();
      });
      observer.observe(root === document ? document.body : root, {
        childList:true,subtree:true,attributes:true,attributeFilter:['href','data-collection-url','data-destination']
      });
      roots.set(root,observer);
      scan(root);
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(() => {
      scheduled = false;
      for (const [root,observer] of roots) {
        if (root.host && !root.host.isConnected) { observer.disconnect();roots.delete(root); }
        else scan(root);
      }
      for (const [node] of bindings) if (!node.isConnected) bindings.delete(node);
      for (const [node] of controllers) if (!node.isConnected) controllers.delete(node);
    }, 100);
  }

  function refresh(recheck = false) {
    if (recheck) { generation++;counts.clear(); }
    schedule();
  }
  window.HBEmptyCollections = {refresh,registerShadow:host=>host.shadowRoot && register(host.shadowRoot)};
  function start() {
    register(document);
    document.addEventListener('shopify:section:load',()=>refresh(true));
    window.addEventListener('pageshow',event=>{if(event.persisted)refresh(true);});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true});
  else start();
})();

